import React, { createContext, useContext, useState, useRef } from 'react';
import { bodyFont, displayFont, getTokens, iri, getIriSoft } from './tokens';

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

export default function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('xceller_theme') || 'dark');
  const [themeTransition, setThemeTransition] = useState(false);
  const themeTransRef = useRef(false);

  const isDark = theme === 'dark';
  const t = getTokens(isDark);
  const iriSoft = getIriSoft(isDark);

  const toggleTheme = () => {
    if (themeTransRef.current) return;
    themeTransRef.current = true;
    setThemeTransition(true);
    setTimeout(() => setTheme(prev => { const next = prev === 'dark' ? 'light' : 'dark'; localStorage.setItem('xceller_theme', next); return next; }), 300);
    setTimeout(() => { setThemeTransition(false); themeTransRef.current = false; }, 900);
  };

  const glassCard = {
    background: [
      isDark
        ? 'linear-gradient(145deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.055) 26%, rgba(125,249,198,0.03) 58%, rgba(143,183,255,0.06) 100%)'
        : 'linear-gradient(145deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.70) 30%, rgba(143,183,255,0.14) 68%, rgba(125,249,198,0.10) 100%)',
      isDark
        ? 'radial-gradient(circle at top left, rgba(255,255,255,0.12), transparent 40%)'
        : 'radial-gradient(circle at top left, rgba(255,255,255,0.95), transparent 42%)',
      isDark
        ? 'linear-gradient(118deg, transparent 54%, rgba(111,168,255,0.16) 70%, rgba(125,249,198,0.10) 82%, rgba(242,201,120,0.12) 92%, transparent 100%)'
        : 'linear-gradient(118deg, transparent 48%, rgba(111,168,255,0.16) 66%, rgba(125,249,198,0.12) 80%, rgba(242,201,120,0.12) 92%, transparent 100%)',
      t.cardBg,
    ].join(', '),
    backdropFilter: 'blur(34px) saturate(1.35)',
    WebkitBackdropFilter: 'blur(34px) saturate(1.35)',
    border: `1px solid ${t.cardBorder}`,
    borderRadius: 24,
    transition: 'all 0.3s ease',
    boxShadow: `${t.iriShadow}, inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.72)'}, inset 0 -24px 44px ${isDark ? 'rgba(255,255,255,0.018)' : 'rgba(255,255,255,0.18)'}`,
    position: 'relative',
    overflow: 'hidden',
    isolation: 'isolate',
  };
  const glassInput = {
    background: [
      isDark
        ? 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))'
        : 'linear-gradient(180deg, rgba(255,255,255,0.88), rgba(255,255,255,0.56))',
      isDark
        ? 'linear-gradient(118deg, transparent 55%, rgba(111,168,255,0.12) 74%, rgba(125,249,198,0.08) 90%, transparent 100%)'
        : 'linear-gradient(118deg, transparent 48%, rgba(111,168,255,0.10) 70%, rgba(125,249,198,0.08) 88%, transparent 100%)',
      t.inputBg,
    ].join(', '),
    backdropFilter: 'blur(22px) saturate(1.25)',
    WebkitBackdropFilter: 'blur(22px) saturate(1.25)',
    border: `1px solid ${t.inputBorder}`,
    borderRadius: 16,
    color: t.text,
    transition: 'all 0.2s',
    width: '100%',
    padding: '12px 14px',
    fontSize: 13,
    outline: 'none',
    boxShadow: `inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.70)'}`,
  };

  const globalCSS = `
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Space+Grotesk:wght@400;500;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html { background: ${t.bg}; }
    body { font-family: ${bodyFont}; background: ${t.bg}; }
    h1, h2, h3, h4, .page-title, .section-title, .hero-title, .price-tag, .brand-display, .iri-text {
      font-family: ${displayFont};
      letter-spacing: -0.03em;
    }
    @keyframes iriShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
    @keyframes ambientDriftA { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(-3vw, 2vh, 0) scale(1.08); } }
    @keyframes ambientDriftB { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(3vw, -2vh, 0) scale(0.96); } }
    .iri-border { position: relative; }
    .iri-border::before {
      content: ''; position: absolute; inset: -1px; border-radius: inherit; padding: 1px;
      background: ${iriSoft}; background-size: 300% 300%; animation: iriShift 6s ease infinite;
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude;
      pointer-events: none; opacity: ${isDark ? 0.66 : 0.78};
    }
    .prism-panel, .prism-panel-soft {
      position: relative;
      overflow: hidden;
      isolation: isolate;
    }
    .prism-panel > *, .prism-panel-soft > * {
      position: relative;
      z-index: 1;
    }
    .prism-panel::after, .prism-panel-soft::after {
      content: '';
      position: absolute;
      pointer-events: none;
      mix-blend-mode: screen;
      border-radius: 999px;
    }
    .prism-panel::after {
      top: 9%;
      bottom: 10%;
      right: -18%;
      width: 42%;
      background: linear-gradient(180deg, rgba(255,255,255,0), rgba(111,168,255,0.20) 26%, rgba(125,249,198,0.14) 52%, rgba(242,201,120,0.16) 76%, rgba(255,255,255,0));
      filter: blur(18px);
      opacity: ${isDark ? 0.92 : 0.65};
      transform: rotate(12deg);
    }
    .prism-panel-soft::after {
      top: 16%;
      bottom: 16%;
      right: -22%;
      width: 36%;
      background: linear-gradient(180deg, rgba(255,255,255,0), rgba(143,183,255,0.16) 28%, rgba(125,249,198,0.10) 58%, rgba(242,201,120,0.12) 82%, rgba(255,255,255,0));
      filter: blur(16px);
      opacity: ${isDark ? 0.74 : 0.48};
      transform: rotate(10deg);
    }
    .iri-text {
      background: ${iri}; background-size: 300% 300%; animation: iriShift 4s ease infinite;
      -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    }
    .iri-line { background: ${iri}; background-size: 300% 300%; animation: iriShift 4s ease infinite; }
    @keyframes themeWipe {
      0%   { opacity: 0; transform: scale(0.3); filter: blur(40px); }
      40%  { opacity: 1; transform: scale(1.1); filter: blur(0px); }
      70%  { opacity: 0.8; transform: scale(1.15); filter: blur(0px); }
      100% { opacity: 0; transform: scale(1.6); filter: blur(20px); }
    }
    .theme-wipe { animation: themeWipe 0.9s cubic-bezier(0.16,1,0.3,1) both; }
    @keyframes themeFlash { 0% { opacity: 0; } 30% { opacity: 0.15; } 100% { opacity: 0; } }
    .theme-flash { animation: themeFlash 0.9s ease both; }
    @keyframes orbFloat1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(40px,-30px) scale(1.05); } 66% { transform: translate(-20px,20px) scale(0.95); } }
    @keyframes orbFloat2 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(-30px,40px) scale(0.95); } 66% { transform: translate(30px,-20px) scale(1.08); } }
    @keyframes orbFloat3 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(25px,35px) scale(1.03); } }
    @keyframes slideDown { from { opacity:0; transform:translateY(-12px); } to { opacity:1; transform:translateY(0); } }
    @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
    @keyframes cardPop { from { opacity:0; transform:translateY(20px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
    .card-enter { animation: cardPop 0.4s cubic-bezier(0.16,1,0.3,1) both; }
    @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}; border-radius: 10px; }
    ::-webkit-scrollbar-thumb { background: ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'}; border-radius: 10px; border: 1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}; }
    ::-webkit-scrollbar-thumb:hover { background: ${isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'}; }
    ::placeholder { color: ${t.textFaint}; }
    a { text-decoration: none; color: inherit; }
    @keyframes spin { to { transform:rotate(360deg); } }
    .spin { animation: spin 1s linear infinite; }
    .ambient-orb {
      position: fixed;
      border-radius: 999px;
      pointer-events: none;
      mix-blend-mode: screen;
      filter: blur(16px);
      z-index: 0;
    }
    .ambient-orb-a {
      width: min(36vw, 520px);
      height: min(36vw, 520px);
      top: -14vh;
      right: -10vw;
      background: radial-gradient(circle, ${isDark ? 'rgba(143,183,255,0.22)' : 'rgba(143,183,255,0.20)'} 0%, transparent 68%);
      animation: ambientDriftA 22s ease-in-out infinite;
    }
    .ambient-orb-b {
      width: min(34vw, 480px);
      height: min(34vw, 480px);
      bottom: -16vh;
      left: -12vw;
      background: radial-gradient(circle, ${isDark ? 'rgba(125,249,198,0.16)' : 'rgba(125,249,198,0.16)'} 0%, transparent 70%);
      animation: ambientDriftB 26s ease-in-out infinite;
    }
    .ambient-grid {
      position: fixed;
      inset: 0;
      z-index: 0;
      pointer-events: none;
      opacity: ${isDark ? 0.14 : 0.10};
      background-image: linear-gradient(${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(13,18,23,0.04)'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(13,18,23,0.04)'} 1px, transparent 1px);
      background-size: 48px 48px;
      mask-image: linear-gradient(180deg, rgba(0,0,0,0.28), rgba(0,0,0,1) 18%, rgba(0,0,0,1) 82%, rgba(0,0,0,0.30));
    }
    @media (max-width: 768px) {
      .hide-mobile { display: none !important; }
      .show-mobile-flex { display: flex !important; }
      .grid-mobile-1 { grid-template-columns: 1fr !important; }
      .grid-mobile-2 { grid-template-columns: 1fr 1fr !important; }
      .mobile-drawer { box-shadow: 0 8px 32px rgba(0,0,0,0.25); }
      .hero-title { font-size: 34px !important; }
      .hero-sub { font-size: 15px !important; }
      .section-title { font-size: 26px !important; }
      .page-title { font-size: 30px !important; }
      .price-tag { font-size: 32px !important; }
      .mission-grid { grid-template-columns: 1fr !important; }
      .admin-users-grid { grid-template-columns: 40px 1fr 1fr auto !important; }
      .admin-users-header { grid-template-columns: 40px 1fr 1fr auto !important; }
      .footer-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
    }
  `;

  const value = { theme, isDark, toggleTheme, t, iri, iriSoft, themeTransition, glassCard, glassInput, globalCSS };

  return (
    <ThemeContext.Provider value={value}>
      <style>{globalCSS}</style>
      {/* Ambient background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: t.ambientGradient, transition: 'background 0.8s ease' }} />
      <div className="ambient-orb ambient-orb-a" />
      <div className="ambient-orb ambient-orb-b" />
      <div className="ambient-grid" />
      {/* Theme transition overlays */}
      {themeTransition && (
        <>
          <div className="theme-wipe" style={{ position: 'fixed', inset: '-20%', zIndex: 200, pointerEvents: 'none', borderRadius: '50%',
            background: 'conic-gradient(from 0deg, rgba(167,139,250,0.5), rgba(96,165,250,0.5), rgba(52,211,153,0.5), rgba(251,191,36,0.5), rgba(244,114,182,0.5), rgba(167,139,250,0.5))',
          }} />
          <div className="theme-flash" style={{ position: 'fixed', inset: 0, zIndex: 199, pointerEvents: 'none', background: isDark ? 'white' : '#060918' }} />
        </>
      )}
      <div style={{ minHeight: '100vh', background: t.bg, fontFamily: bodyFont, color: t.text, position: 'relative', transition: 'background 0.6s ease, color 0.4s ease' }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}
